<template>
  <section class="horizontal-menu-sticky"
    :class="`has-hero-text-${$theme.name} ${`theme-${$theme.name}`}`">
    <div class="hero-body paddingless hanger">
      <div class="horizontal-menu-container">
        <module-sub-nav :menu-items="visibleMenuItems"
          :menu-items-more="visibleMoreMenuItems"
          @item-click="handleItemClick"
          @custom-action="handleCustomAction">
          <template #menu-item-actions="{ item }">
            <span v-if="item.label === 'Images' && imageCount > 0"
              class="icon breakout-icon-hover is-clickable"
              @click.stop.prevent="handleClickImageBreakOut"
              v-tippy="{ enabled: true }"
              content="Image Breakout">
              <span class="mso mso-16px">open_in_new</span>
            </span>
          </template>
        </module-sub-nav>
      </div>
    </div>
  </section>
</template>

<script>
import ModuleSubNav from '@/components/ModuleSubNav'
import QuoteRoutes from './route-types'
import { QuotingMethodTypes, InvoiceTypes, EventHubTypes } from '@/enums'
import Guid from '@/components/Guid'
import QuoteService from './QuoteService'
import { SmsService, WhatsAppService, EmailService } from '@/services'

export default {
  name: 'QuoteSubNav',
  inject: ['$vv'],
  components: {
    ModuleSubNav
  },
  props: {
    value: null,
    insurer: {
      type: Object,
      default: () => null
    },
    ormUnreadMessageCount: {
      type: Number,
      default: 0
    }
  },
  data() {
    return {
      imageCount: 0,
      documentCount: 0,
      unreadInterval: null,
      unreadCount: 0
    }
  },
  computed: {
    $v() {
      return this.$vv || this.$v
    },
    visibleMenuItems() {
      return this.menuItems
        .filter((item) => item.visible)
        .map((item) => ({
          ...item,
          // Convert badgeClass object to string for ModuleSubNav
          badgeClass: this.convertBadgeClass(item.badgeClass)
        }))
    },
    visibleMoreMenuItems() {
      return this.menuItemsMore
        .filter((item) => item.visible)
        .map((item) => ({
          ...item,
          // Convert badgeClass object to string for ModuleSubNav
          badgeClass: this.convertBadgeClass(item.badgeClass)
        }))
    },
    routeTypes() {
      return QuoteRoutes
    },
    quotingMethodTypes() {
      return QuotingMethodTypes
    },
    enableItemRoutes() {
      return Guid.validGuid(this.value.insurerId) && Guid.validGuid(this.value.vehicleId)
    },
    pnetAudanetMenuText() {
      if (!this.value.isAudaNet) return ''

      const taskType = this.value.audatex?.taskType
      if (taskType) {
        return this.value.isAudaBridge ? `${taskType}` : `/${taskType}`
      }

      return '/AudaNet'
    },
    others() {
      return this.value.others.filter((i) => !i.deleted && i.itemType !== 'MECH')
    },
    invoice() {
      return this.value.invoices.find((i) => i.invoiceType === InvoiceTypes.Quote && !i.deleted)
    },
    excessInvoice() {
      return this.value.invoices.find((i) => (i.invoiceType === InvoiceTypes.Excess || i.invoiceType === InvoiceTypes.OwnerContribution) && !i.deleted)
    },
    remarkCount() {
      return this.value && this.value.remarks ? this.value.remarks.filter((r) => !r.isDeleted).length : 0
    },
    menuItems() {
      const isNew = this.value.isNew
      return [
        {
          label: 'Header',
          route: { name: this.routeTypes.QuoteHeader.name, params: { quoteId: this.$route.params.quoteId }, query: this.$route.query },
          disabled: false,
          visible: true,
          badgeClass: {
            'has-badge-danger has-badge-small has-badge-rounded':
              this.$v.estimateJobStartEnd.$error ||
              this.$v.quoteHeaderGroup.$error ||
              this.$v.ormHeaderGroup.$error ||
              this.$v.audaNetHeaderGroup.$error ||
              this.$v.ormSummaryGroup.$error
          },
          badge:
            this.$v.estimateJobStartEnd.$error ||
            this.$v.quoteHeaderGroup.$error ||
            this.$v.ormHeaderGroup.$error ||
            this.$v.audaNetHeaderGroup.$error ||
            this.$v.ormSummaryGroup.$error
              ? ''
              : null
        },
        {
          label: 'Quoting',
          route: { name: this.routeTypes.QuoteBuilder.name, params: { quoteId: this.$route.params.quoteId }, query: this.$route.query },
          disabled: !this.enableItemRoutes || isNew,
          visible: true,
          badgeClass: { 'has-badge-rounded has-badge-medium has-badge-danger': true },
          badge: null
        },
        {
          label: 'Parts',
          route: { name: this.routeTypes.QuotePartsControl.name, params: { quoteId: this.$route.params.quoteId }, query: this.$route.query },
          disabled: !this.enableItemRoutes || isNew,
          visible: this.$company.setting.partsControlEnabled,
          badgeClass: { 'has-badge-rounded has-badge-medium has-badge-danger': true },
          badge: null
        },
        {
          label: 'Images',
          route: { name: this.routeTypes.QuoteImages.name, params: { quoteId: this.$route.params.quoteId }, query: this.$route.query },
          disabled: isNew,
          visible: true,
          badgeClass: { 'has-badge-info has-badge-rounded has-badge-medium': this.imageCount },
          badge: this.imageCount ? this.imageCount : null
        },
        {
          label: 'Assessments',
          route: { name: this.routeTypes.AuthoriseView.name, params: { quoteId: this.$route.params.quoteId }, query: this.$route.query },
          disabled: !this.enableItemRoutes || isNew,
          visible: this.value.isAssessmentEnabled,
          badgeClass: { 'has-badge-rounded has-badge-medium has-badge-danger': true },
          badge: null
        },
        {
          label: (this.value.isAudaBridge) ? this.pnetAudanetMenuText : `PNET${this.pnetAudanetMenuText}`,
          route: { name: this.routeTypes.QuotePnetAudanet.name, params: { quoteId: this.$route.params.quoteId }, query: this.$route.query },
          disabled: isNew,
          visible: this.value.audatex || this.value.pnet
        },
        {
          label: 'ORM',
          route: { name: this.routeTypes.QuoteOrm.name, params: { quoteId: this.$route.params.quoteId }, query: this.$route.query },
          disabled: isNew,
          visible: this.value.orm || false,
          badgeClass: { 'has-badge-rounded has-badge-medium has-badge-info': this.ormUnreadMessageCount > 0 },
          badge: this.ormUnreadMessageCount > 0 ? this.ormUnreadMessageCount : null
        },
        {
          label: 'ARNIE',
          route: { name: this.routeTypes.QuoteArnie.name, params: { quoteId: this.$route.params.quoteId }, query: this.$route.query },
          disabled: isNew,
          visible: this.value.arnieCount
        },
        {
          label: 'EstImage',
          route: { name: this.routeTypes.QuoteEstImage.name, params: { quoteId: this.$route.params.quoteId }, query: this.$route.query },
          disabled: isNew,
          visible: this.insurer?.estimage,
          badgeClass: { 'has-badge-rounded has-badge-medium has-badge-danger': true },
          badge: null
        },
        {
          label: 'Summary',
          route: { name: this.routeTypes.QuoteSummary.name, params: { quoteId: this.$route.params.quoteId }, query: this.$route.query },
          disabled: isNew,
          visible: true,
          badgeClass: null, //{ 'has-badge-danger has-badge-small has-badge-rounded': this.$v.ormSummaryGroup.$error || this.$v.estimateJobStartEnd.$error },
          badge: null //this.$v.ormSummaryGroup.$error || this.$v.estimateJobStartEnd.$error ? '' : null
        },
        {
          label: 'Invoice',
          route: {
            name: this.value.isAssessmentEnabled ? this.routeTypes.QuoteAssessmentInvoice.name : this.routeTypes.QuoteInvoice.name,
            params: { quoteId: this.$route.params.quoteId },
            query: this.$route.query
          },
          disabled: !this.enableItemRoutes || isNew,
          visible: true,
          //badgeClass: { 'has-badge-rounded has-badge-medium has-badge-icon-check': !!(this.invoice) },
          badgeClass: function () {
            const classes = this.getInvoiceBadgeClass('Invoice')
            return {
              'has-badge-rounded has-badge-small has-badge-icon-check': !!(this.invoice),
              ...classes
            }
          },
          badge: this.invoice ? '✓' : null
        },
        {
          label: 'Excess',
          route: { name: this.routeTypes.QuoteExcessInvoice.name, params: { quoteId: this.$route.params.quoteId }, query: this.$route.query },
          disabled: !this.enableItemRoutes || isNew,
          visible: true,
          badgeClass: function () {
            const classes = this.getInvoiceBadgeClass('Excess')
            return {
              'has-badge-rounded has-badge-small has-badge-icon-check': !!(this.excessInvoice),
              ...classes
            }
          },
          badge: this.excessInvoice ? '✓' : null
        },
        {
          label: 'Docs',
          route: { name: this.routeTypes.QuoteDocuments.name, params: { quoteId: this.$route.params.quoteId }, query: this.$route.query },
          disabled: isNew,
          visible: true,
          badgeClass: { 'has-badge-info has-badge-rounded has-badge-medium': this.documentCount },
          badge: this.documentCount ? this.documentCount : null
        },
        {
          label: 'Comms',
          route: { name: this.routeTypes.QuoteCommunications.name, params: { quoteId: this.$route.params.quoteId }, query: this.$route.query },
          disabled: isNew,
          visible: true,
          badgeClass: { 'has-badge-info has-badge-rounded has-badge-medium': this.unreadCount },
          badge: this.unreadCount ? this.unreadCount : null
        },
        {
          label: 'Remarks',
          route: { name: this.routeTypes.QuoteRemarks.name, params: { quoteId: this.$route.params.quoteId }, query: this.$route.query },
          disabled: isNew,
          visible: true,
          badgeClass: { 'has-badge-info has-badge-rounded has-badge-medium': this.remarkCount },
          badge: this.remarkCount ? this.remarkCount : null
        },
        {
          label: 'Rates & Markups',
          route: { name: this.routeTypes.QuoteRatesMarkups.name, params: { quoteId: this.$route.params.quoteId }, query: this.$route.query },
          disabled: isNew,
          visible: true
        },
        {
          label: 'Annotations',
          route: { name: this.routeTypes.QuoteAnnotations.name, params: { quoteId: this.$route.params.quoteId }, query: this.$route.query },
          disabled: isNew,
          visible: true
        },
        {
          label: 'Other Labour',
          route: { name: this.routeTypes.QuoteOtherLabour.name, params: { quoteId: this.$route.params.quoteId }, query: this.$route.query },
          disabled: !this.enableItemRoutes || isNew,
          visible: !!this.others.length,
          badgeClass: { 'has-badge-rounded has-badge-medium': this.others.length },
          badge: this.others.length ? this.others.length : null
        },
        {
          label: 'RepX',
          route: { name: this.routeTypes.QuoteRepx.name, params: { quoteId: this.$route.params.quoteId }, query: this.$route.query },
          disabled: isNew,
          visible: this.value.repx
        }
      ]
    },
    menuItemsMore() {
      // const isNew = this.value.isNew
      return [

      ]
    }
  },
  async created() {
    this.getImageCount()
    this.getDocumentCount()
    this.getUnreadCount()
    this.autoRefreshUnreadCount()
  },
  mounted() {
    this.$eventHub.$on(EventHubTypes.ImageCountChanged, (count) => (this.imageCount = count))
    this.$eventHub.$on(EventHubTypes.DocumentCountChanged, (count) => (this.documentCount = count))
  },
  beforeDestroy() {
    this.$eventHub.$off(EventHubTypes.ImageCountChanged)
    this.$eventHub.$off(EventHubTypes.DocumentCountChanged)
    clearInterval(this.unreadInterval)
  },
  methods: {
    convertBadgeClass(badgeClass) {
      if (!badgeClass) return ''

      const resolved = typeof badgeClass === 'function' ? badgeClass.call(this) : badgeClass

      if (typeof resolved === 'string') return resolved

      if (Array.isArray(resolved)) {
        const parts = []
        resolved.forEach((entry) => {
          if (!entry) return
          if (typeof entry === 'string') {
            parts.push(entry)
          } else if (typeof entry === 'object') {
            parts.push(
              Object.keys(entry)
                .filter((key) => entry[key])
                .join(' ')
            )
          }
        })
        return parts.filter(Boolean).join(' ').trim()
      }

      if (typeof resolved === 'object') {
        return Object.keys(resolved)
          .filter((key) => resolved[key])
          .join(' ')
      }

      return ''
    },
    handleItemClick(item) {
      this.goToRoute(item.route, item.disabled)
    },
    handleCustomAction(payload) {
      // Handle custom actions emitted from ModuleSubNav slots
      const { item, actionType, args } = payload
      this.$emit('custom-action', { item, actionType, args })
    },
    goToRoute(route, isDisabled) {
      if (isDisabled) {
        this.$toast.open({
          message: 'Please create the quote to make changes',
          type: 'is-warning',
          position: 'is-top'
        })
        return
      }
      this.$router.push(route)
    },
    async getImageCount() {
      const imageIds = await QuoteService.getImageIds(this.value.quoteId)
      this.imageCount = imageIds.length
    },
    async getDocumentCount() {
      const attachments = await QuoteService.getQuoteAttachments(this.value.quoteId)
      let count = Object.values(attachments.data).reduce((total, row) => total + Object.values(row.quoteAttachments).length, 0)
      this.documentCount = count
    },
    async getUnreadCount() {
      const smsreadCount = await SmsService.getUnreadCount(this.value.quoteId)
      const wareadCount = await WhatsAppService.getUnreadCount(this.value.quoteId)
      const emailreadCount = await EmailService.getUnreadCount(this.value.quoteId)
      this.unreadCount = smsreadCount + wareadCount + emailreadCount
    },
    autoRefreshUnreadCount() {
      this.unreadInterval = setInterval(async () => {
        this.getUnreadCount()
      }, 30000)
    },
    handleClickImageBreakOut() {
      const baseUri = process.env.VUE_APP_BASE_URI === '/' ? process.env.VUE_APP_BASE_URI : `${process.env.VUE_APP_BASE_URI}/`
      const newWindow = window.open(`${baseUri}quotes/${this.value.quoteId}/imagesbreakout?uninterruptedview=true`, 'Imageviewer', {
        width: 1600,
        height: 900,
        left: 0,
        top: 0,
        fullscreen: 'yes',
        title: 'Image Breakout'
      })
      if (newWindow) {
        const timer = setInterval(() => {
          if (newWindow.closed) {
            clearInterval(timer)
            if (this.$router.currentRoute.name == QuoteRoutes.QuoteImages.name) {
              this.$emit('reload-quote-images')
            }
          }
        }, 500)
      }
    },
    getInvoiceBadgeClass(type) {
      let classes = {}

      if (type === 'Invoice') {
        if (this.$v.entity.invoices.$error) {
          let errors = Object.values(this.$v.entity['invoices'].$each.$iter).filter((r) => {
            return r.$anyError
          })
          errors.forEach((item) => {
            if (item.$model.invoiceType === InvoiceTypes.Quote) {
              classes['has-badge-danger'] = true
            }
          })
        } else if (this.invoice && this.invoice.isNew) {
          classes['has-badge-gold'] = true
        } else {
          classes['has-badge-success'] = true
        }
      }
      if (type === 'Excess') {
        if (this.$v.entity.invoices.$error) {
          let errors = Object.values(this.$v.entity['invoices'].$each.$iter).filter((r) => {
            return r.$anyError
          })
          errors.forEach((item) => {
            if (item.$model.invoiceType === InvoiceTypes.Excess || item.$model.invoiceType === InvoiceTypes.OwnerContribution) {
              classes['has-badge-danger'] = true
            }
          })
        } else if (this.excessInvoice && this.excessInvoice.isNew) {
          classes['has-badge-gold'] = true
        } else {
          classes['has-badge-success'] = true
        }
      }
      return classes
    }
  }
}
</script>

<style lang="scss" scoped>
.horizontal-menu-sticky {
  position: sticky;
  top: 64px;
  z-index: 30;
  padding: 0 0.75rem 0.75rem 0.75rem;
  // overflow-x: auto;
  // background-color: #f0f2f4; //#fff;
}

.horizontal-menu-container {
  width: 100%;
  padding: 0 0.75rem 0.5rem 0.75rem;
  position: relative;
  min-width: max-content;

  ::v-deep [class*='has-badge-'] {
    position: relative;
    z-index: 25;

    &::after {
      position: absolute;
      z-index: 30 !important;
      pointer-events: none;
    }
  }

  ::v-deep .has-badge-medium::after {
    min-height: 1.15em;
    font-size: 0.85em;
    margin-left: 0.25em;
    z-index: 30 !important;
  }

  ::v-deep .has-badge-small::after,
  ::v-deep .has-badge-medium::after,
  ::v-deep .has-badge-large::after,
  ::v-deep .has-badge-info::after,
  ::v-deep .has-badge-success::after,
  ::v-deep .has-badge-warning::after,
  ::v-deep .has-badge-danger::after,
  ::v-deep .has-badge-rounded::after,
  ::v-deep .has-badge-icon-check::after {
    z-index: 30 !important;
    position: absolute !important;
  }
}

@media screen and (max-width: 768px) {
  .horizontal-menu-sticky {
    // overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }
}

.hanger {
  background-color: var(--background-color);
}

</style>